import {
  REFERRAL_NAME_MAX,
  REFERRAL_NOTE_MAX,
  REFERRAL_OTHER_MAX,
  type ReferralContactKind,
  type ReferralContactProblem,
} from "@edu/shared";
import type { ApiErrorCode } from "../../lib/api.ts";
import { pick } from "../../lib/plurals.ts";

/**
 * Слова окна «Позвать коллегу» (`docs/colleague-referral-design.md`, 6.2).
 *
 * Чистые функции по проблеме и по коду, а не по исключению: так их проверяет
 * `referral-text.test.ts` без клиента API и без React. Проблемы приходят из
 * трёх проверок `@edu/shared` (`checkReferralName`, `normalizeReferralContact`,
 * `checkReferralNote`), тех же, что держат схему сервера, поэтому подсказка под
 * полем и отказ сервера не расходятся.
 *
 * ⚠️ Тексты перенесены из проекта буквально. Правка любого из них это проход
 * скилла `teacher-texts`, а не вкус исполнителя. Числа в текстах берутся из
 * констант, а не пишутся на глаз.
 *
 * ⚠️ Текст о потолке открытых заявок говорит о НАС, а не о преподавателе: пять
 * заявок висят, потому что мы до них не дошли. Ни «лимита», ни числа.
 */

/** Подписи кнопок типа контакта, в порядке показа: Telegram первым (6.2). */
export const CONTACT_KINDS: readonly { id: ReferralContactKind; label: string }[] = [
  { id: "telegram", label: "Telegram" },
  { id: "email", label: "Почта" },
  { id: "phone", label: "Телефон" },
  { id: "other", label: "Другое" },
];

/** Подпись поля контакта: меняется вместе с типом. */
export const CONTACT_LABEL: Record<ReferralContactKind, string> = {
  telegram: "Ник в Telegram",
  email: "Адрес почты",
  phone: "Номер телефона",
  other: "Где найти коллегу",
};

/** Подсказка в пустом поле контакта. */
export const CONTACT_PLACEHOLDER: Record<ReferralContactKind, string> = {
  telegram: "@nick",
  email: "name@mail.ru",
  phone: "+7 900 123-45-67",
  other: "Например: ссылка на страницу ВКонтакте или номер в MAX",
};

/**
 * Строка под полем Telegram. Показывается всегда, пока нет ошибки; ошибка
 * встаёт на её место (слот под полем один).
 */
export const TELEGRAM_LINK_HINT = "Можно вставить ссылку вида t.me/nick";

const INVISIBLE = "В тексте есть невидимые символы. Сотрите поле и наберите заново";

export function nameProblemText(problem: "empty" | "too_long" | "control_chars"): string {
  switch (problem) {
    case "empty":
      return "Напишите, как зовут коллегу";
    case "too_long":
      return `Слишком длинно: имя помещается в ${REFERRAL_NAME_MAX} знаков`;
    case "control_chars":
      return INVISIBLE;
  }
}

/**
 * Подсказка под контактом. `too_long` бывает только у «другого»: у почты,
 * ника и телефона длину ловит проверка формы.
 */
export function contactProblemText(problem: ReferralContactProblem): string {
  switch (problem) {
    case "empty":
      return "Напишите, как связаться с коллегой";
    case "email_shape":
      return "Такой адрес не подходит. Адрес пишется так: name@mail.ru. Если в адресе русские буквы, выберите «Другое»";
    case "telegram_shape":
      return "Не похоже на ник в Telegram. Ник пишется латиницей, например @irina_math. Если ника нет, выберите «Телефон»";
    case "phone_shape":
      return "Проверьте номер: похоже, в нём лишние знаки или не хватает цифр";
    case "too_long":
      return `Слишком длинно: сюда помещается не больше ${REFERRAL_OTHER_MAX} знаков. Хватит одной ссылки`;
    case "control_chars":
      return INVISIBLE;
  }
}

/**
 * Подсказка под комментарием.
 *
 * `maxLength` у поля нет, поэтому перебор длины живой: вставленный длинный
 * текст не обрезается молча, а получает эту подсказку. Текст сложен по образцу
 * подсказки к имени.
 */
export function noteProblemText(problem: "too_long" | "control_chars"): string {
  switch (problem) {
    case "too_long":
      return `Слишком длинно: комментарий помещается в ${REFERRAL_NOTE_MAX} знаков`;
    case "control_chars":
      return INVISIBLE;
  }
}

/** С какого остатка показывать счётчик комментария: только у края (6.2). */
export const NOTE_COUNTER_FROM = 50;

/**
 * Счётчик комментария: «Остался 1 знак», «Осталось 2 знака», «Осталось 5 знаков».
 * `null`, пока до края далеко: обычный комментарий это одна строка, и счётчик
 * на каждом знаке был бы шумом.
 *
 * Падеж считает общий `pick` из `lib/plurals.ts`, и глагол согласуется по той же
 * последней цифре, что и существительное: «Остался 21 знак».
 */
export function noteLeftText(left: number): string | null {
  if (left >= NOTE_COUNTER_FROM) return null;
  const n = Math.max(0, left);
  return `${pick(n, "Остался", "Осталось", "Осталось")} ${n} ${pick(n, "знак", "знака", "знаков")}`;
}

/**
 * Отказ отправки. `null` это «ответа нет вовсе»: обрыв сети, недоступный API.
 *
 * ⚠️ Отказ по стороне (сессия по ссылке ученика) это общий `not_found` и
 * попадает в последнюю ветку: пункта у такой сессии нет, и дойти до окна она
 * не может.
 */
export function sendErrorText(code: ApiErrorCode | null): string {
  if (code === null) {
    return "Не получилось отправить: нет связи с сервером. Проверьте интернет и нажмите «Отправить» ещё раз";
  }
  if (code === "referral_limit") {
    return "Мы ещё не дошли до коллег, которых вы позвали раньше. Как только дойдём, можно будет позвать следующих.";
  }
  if (code === "invalid_body") {
    return "Не получилось отправить: проверьте поля и нажмите «Отправить» ещё раз";
  }
  return "Не получилось отправить. Попробуйте ещё раз чуть позже";
}
